import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';
import type { SerializableValue } from '@atlassian/rovo-content-bridge-api/command';

export type CreatedSpace = {
	id: string;
	key: string;
};

type CreateKbApi = {
	createConfluenceSpace: typeof createConfluenceSpace;
	canCurrentConfluenceUserCreateSpace: typeof canCurrentConfluenceUserCreateSpace;
	fetchProjectIdentifierById: typeof fetchProjectIdentifierById;
	fetchRovoSkillArticleById: typeof fetchRovoSkillArticleById;
	updateRovoSkillArticleById: typeof updateRovoSkillArticleById;
	deleteRovoSkillArticleById: typeof deleteRovoSkillArticleById;
};

type JiraProjectDetailsResponse = {
	key?: string;
};

type RovoSkillArticleResponse = {
	articleId?: string;
	title?: string;
	body?:
		| SerializableValue
		| {
				value?: SerializableValue;
		  };
	rovoTopicId?: string | null;
};

type RovoSkillArticleUpdateResponse = {
	articleId?: string;
};

type ConfluenceCurrentUserResponse = {
	operations?: {
		operation?: string;
		targetType?: string;
	}[];
};

const JSD_APOLLO_STARGATE_CONTEXT_ROOT = '/gateway/api/jsd-apollo-stargate/sharded/workspace';
const CONFLUENCE_CREATE_SPACE_OPERATION = 'create_space';
const CONFLUENCE_APPLICATION_TARGET_TYPE = 'application';

const toAdfBodyValue = (body: SerializableValue): string =>
	typeof body === 'string' ? body : JSON.stringify(body);

const buildSpaceKey = (spaceName: string): string => {
	const keyPrefix = spaceName
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '')
		.slice(0, 8);
	const randomSuffix = Math.random().toString(36).slice(2, 7).toUpperCase();
	return `${keyPrefix || 'KB'}${randomSuffix}`;
};

const getCreateSpacePath = ({
	projectId,
	activationId,
	spaceName,
	spaceKey,
}: {
	projectId: number;
	activationId: string;
	spaceName: string;
	spaceKey: string;
}): string =>
	`${JSD_APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/v2/project/${projectId}/kb-settings/space?spaceKey=${encodeURIComponent(
		spaceKey,
	)}&spaceName=${encodeURIComponent(spaceName)}`;

const createConfluenceSpace = async ({
	spaceName,
	projectId,
	activationId,
}: {
	spaceName: string;
	projectId: number;
	activationId: string;
}): Promise<CreatedSpace> => {
	const normalizedSpaceName = spaceName.trim();
	const spaceKey = buildSpaceKey(normalizedSpaceName);
	const data = (await performHttpFetch({
		path: getCreateSpacePath({
			projectId,
			activationId,
			spaceName: normalizedSpaceName,
			spaceKey,
		}),
		options: {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		},
	})) as { id?: string | number; key?: string };

	if (!data.id || !data.key) {
		throw new Error('Confluence space created without id');
	}

	return {
		id: String(data.id),
		key: data.key,
	};
};

const canCurrentConfluenceUserCreateSpace = async ({
	cloudId,
}: {
	cloudId: string;
}): Promise<boolean> => {
	try {
		const response = (await performHttpFetch({
			path: `/gateway/api/ex/confluence/${cloudId}/wiki/rest/api/user/current?expand=operations`,
			options: {
				method: 'GET',
				headers: {
					Accept: 'application/json',
				},
				credentials: 'include',
			},
		})) as ConfluenceCurrentUserResponse;

		if (!Array.isArray(response.operations)) {
			return true;
		}

		return response.operations.some(
			({ operation, targetType }) =>
				operation === CONFLUENCE_CREATE_SPACE_OPERATION &&
				targetType === CONFLUENCE_APPLICATION_TARGET_TYPE,
		);
	} catch {
		return true;
	}
};

const fetchProjectIdentifierById = async ({
	projectId,
}: {
	projectId: number;
}): Promise<string> => {
	const response = (await performHttpFetch({
		path: `/rest/api/2/project/${projectId}`,
		options: {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
		},
	})) as JiraProjectDetailsResponse;

	if (!response.key) {
		throw new Error('Project details response missing key');
	}

	return response.key;
};

const fetchRovoSkillArticleById = async ({
	projectId,
	activationId,
	articleId,
}: {
	projectId: number;
	activationId: string;
	articleId: string;
}): Promise<{
	articleId: string;
	title: string;
	body: SerializableValue | undefined;
	rovoTopicId: string;
}> => {
	const response = (await performHttpFetch({
		path: `${JSD_APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/project/${projectId}/rovo-skill-articles/${articleId}`,
		options: {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
		},
	})) as RovoSkillArticleResponse;

	if (!response.articleId || !response.title) {
		throw new Error('Rovo skill article response missing required fields');
	}

	const normalizedBody =
		response.body && typeof response.body === 'object' && 'value' in response.body
			? response.body.value
			: response.body;

	return {
		articleId: response.articleId,
		title: response.title,
		body: normalizedBody,
		rovoTopicId: response.rovoTopicId ?? '',
	};
};

const updateRovoSkillArticleById = async ({
	projectId,
	activationId,
	articleId,
	title,
	body,
	rovoTopicId,
}: {
	projectId: number;
	activationId: string;
	articleId: string;
	title: string;
	body: SerializableValue;
	rovoTopicId?: string | null;
}): Promise<{ articleId: string }> => {
	const response = (await performHttpFetch({
		path: `${JSD_APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/project/${projectId}/rovo-skill-articles/${articleId}`,
		options: {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				title,
				body: { value: toAdfBodyValue(body) },
				...(rovoTopicId !== undefined ? { rovoTopicId } : {}),
			}),
		},
	})) as RovoSkillArticleUpdateResponse;

	if (!response.articleId) {
		throw new Error('Rovo skill article update response missing articleId');
	}

	return { articleId: response.articleId };
};

const deleteRovoSkillArticleById = async ({
	projectId,
	activationId,
	articleId,
}: {
	projectId: number;
	activationId: string;
	articleId: string;
}): Promise<void> => {
	await performHttpFetch({
		path: `${JSD_APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/project/${projectId}/rovo-skill-articles/${articleId}`,
		options: {
			method: 'DELETE',
			headers: {
				Accept: 'application/json',
			},
		},
	});
};

export const createKbApi: CreateKbApi = {
	createConfluenceSpace: createConfluenceSpace,
	canCurrentConfluenceUserCreateSpace: canCurrentConfluenceUserCreateSpace,
	fetchProjectIdentifierById: fetchProjectIdentifierById,
	fetchRovoSkillArticleById: fetchRovoSkillArticleById,
	updateRovoSkillArticleById: updateRovoSkillArticleById,
	deleteRovoSkillArticleById: deleteRovoSkillArticleById,
} as const;
